/** Token armazenado no banco: @[Nome](uuid) */
export const MENTION_TOKEN_REGEX = /@\[([^\]]+)\]\(([0-9a-f-]{36})\)/gi

export function formatMentionToken(user: { id: string; name?: string | null }): string {
  const label = (user.name || 'Usuário').trim()
  return `@[${label}](${user.id})`
}

export interface ActiveMention {
  start: number
  query: string
}

/** Detecta se o cursor está digitando uma menção (@...) */
export function getActiveMention(text: string, cursor: number): ActiveMention | null {
  const before = text.slice(0, cursor)
  const atIndex = before.lastIndexOf('@')
  if (atIndex === -1) return null

  const afterAt = before.slice(atIndex + 1)

  // Dentro de token já formado @[...](...)
  if (afterAt.startsWith('[')) return null

  // Apenas letras, números, espaços e pontuação leve (nomes)
  if (afterAt.length > 0 && !/^[\p{L}\p{N}\s._-]*$/u.test(afterAt)) return null

  // @ colado em palavra anterior (ex: email)
  if (atIndex > 0) {
    const prev = before[atIndex - 1]
    if (prev && !/\s|[([{'"`]/.test(prev)) return null
  }

  return { start: atIndex, query: afterAt }
}

export function insertMentionAt(
  text: string,
  mention: ActiveMention,
  user: { id: string; name?: string | null }
): { text: string; cursor: number } {
  const token = formatMentionToken(user)
  const before = text.slice(0, mention.start)
  const after = text.slice(mention.start + 1 + mention.query.length)
  const next = `${before}${token} ${after}`
  const cursor = before.length + token.length + 1
  return { text: next, cursor }
}

export type MentionTextPart =
  | { type: 'text'; value: string }
  | { type: 'mention'; name: string; userId: string }

export function parseMentionContent(content: string): MentionTextPart[] {
  const parts: MentionTextPart[] = []
  let lastIndex = 0
  const regex = new RegExp(MENTION_TOKEN_REGEX.source, 'gi')
  let match: RegExpExecArray | null

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: content.slice(lastIndex, match.index) })
    }
    parts.push({ type: 'mention', name: match[1], userId: match[2] })
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < content.length) {
    parts.push({ type: 'text', value: content.slice(lastIndex) })
  }

  return parts.length > 0 ? parts : [{ type: 'text', value: content }]
}

/** Extrai UUIDs únicos de menções no conteúdo (ordem de aparição). */
export function extractMentionUserIds(content: string): string[] {
  const ids: string[] = []
  const seen = new Set<string>()
  const regex = new RegExp(MENTION_TOKEN_REGEX.source, 'gi')
  let match: RegExpExecArray | null

  while ((match = regex.exec(content)) !== null) {
    const userId = match[2]
    if (!seen.has(userId)) {
      seen.add(userId)
      ids.push(userId)
    }
  }

  return ids
}

/** Converte tokens de menção em @Nome legível para preview. */
export function mentionPreviewText(content: string, maxLen = 160): string {
  const readable = content.replace(
    new RegExp(MENTION_TOKEN_REGEX.source, 'gi'),
    (_match, name: string) => `@${name}`
  )
  const line = readable.replace(/\s+/g, ' ').trim()
  return line.length > maxLen ? `${line.slice(0, maxLen)}…` : line
}
